import { AuthHeroPanel } from './AuthHeroPanel';
import type { ReactNode } from 'react';
import { config } from '@/shared/config/env';

/**
 * The shared frame for full-page auth screens.
 *
 * Extracted from LoginPage and SignUpPage, which were the only two screens
 * that had it. The later screens in the same flow — email confirmation, the
 * mandatory two-factor setup — were built against shadcn primitives instead,
 * and the seam showed: the panel and logo matched, then the heading sat
 * left-aligned against a top-anchored column while the first two screens
 * centred theirs, and the primary action was purple where the first two were
 * black. Same flow, two design languages, switching mid-way.
 *
 * WHICH VARIANT THIS IS. Login and SignUp differ in one real way. Login pins
 * `h-screen overflow-hidden` and positions its column `absolute inset-0`,
 * which centres perfectly and CLIPS anything taller than the viewport. SignUp
 * scrolls: `overflow-y-auto` with a sticky logo and a `flex-1` column. This is
 * the SignUp variant, because it degrades correctly in both directions —
 * short content centres exactly as before, and content that outgrows the
 * viewport scrolls instead of being cut off. The two-factor screen grows: an
 * error banner, a list of enrolled methods, a code field.
 *
 * LoginPage and SignUpPage deliberately still hold their own copies. They are
 * the reference this was taken from, they are pixel-sensitive, and moving them
 * belongs in its own change where a visual diff is the point. Anything NEW in
 * this flow should use this instead of growing a third copy.
 *
 * The raw hex values, `data-name` attributes and arbitrary Tailwind classes are
 * kept verbatim from the Figma export, matching the convention the two source
 * files state explicitly: these files read as faithful translations rather than
 * theme-token interpretations.
 */

const FONT_SERIF = 'var(--font-brand)';
const FONT_SANS = 'var(--font-brand)';

export interface AuthShellProps {
  children: ReactNode;
  /** Figma-side name, for parity with the other screens in this flow. */
  dataName?: string;
  /**
   * The left-hand visual panel.
   *
   * OPTIONAL, DEFAULTING TO THE ONE EVERY SCREEN HAS TODAY — the same seam as
   * `showFormHints` and `secondPerson` elsewhere in this tree, and for the
   * same reason: a caller that does not pass one is unchanged by this prop
   * existing.
   *
   * It exists because the CLIENT PORTAL's sign-in needs a different panel and
   * an identical everything-else. `AuthHeroPanel` sells the software — "Quant
   * Strategies, Simplified", under "OUR PHILOSOPHY" — to the person who
   * chooses it. A client signing in to see their own money did not choose it,
   * cannot switch it, and is being handed a promise made to somebody else.
   *
   * A prop rather than a variant flag inside the panel: the two dispositions
   * genuinely differ (bottom-left over a product render, against centred over
   * a photograph), and a component holding both would be two render paths
   * behind one name.
   */
  hero?: ReactNode;
}

export function AuthShell({ children, dataName, hero }: AuthShellProps) {
  return (
    <div
      /* `light` PINS THIS SHELL, for the same reason the onboarding screen is
         pinned: the whole sign-in flow is a 1:1 Figma port that is opaque
         white in both themes by design, and every colour in it is a verbatim
         hex. Two widgets inside it are NOT — OtpInput and AuthPrimaryButton
         are shared with Settings > Security, where they have to follow the
         theme — so without this they would render dark-on-white here.

         This also fixes `/security-setup`, which renders the fully tokenised
         MfaEnrollment inside this shell: before the pin, a new account's
         mandatory second-factor screen drew `text-foreground` (near-white in
         dark) onto `bg-white`, and dark `bg-muted` tiles onto the same sheet.
         It is a gate every new account passes through.

         Anything added inside here must use TOKENS, never `dark:` variants:
         the dark variant keys off `:is(.dark *)`, so it still matches inside
         a `.light` island and would fire on a screen that is meant to be
         light. */
      className="light flex items-start relative w-full min-h-screen bg-white [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale]"
      data-name={dataName}
    >
      {hero ?? <AuthHeroPanel />}

      <div
        className="flex-1 bg-white relative h-screen overflow-y-auto overflow-x-hidden min-w-0"
        data-name="wrapper"
      >
        <div className="flex flex-col min-h-full">
          {/* Sticky so it stays put when the column below it scrolls. */}
          <div className="w-full flex justify-center pt-[48px] pb-[16px] sticky top-0 bg-white z-10 shrink-0">
            <div
              className="content-stretch flex gap-[10px] items-center justify-center leading-[0] relative shrink-0"
              data-name="logo"
            >
              <img
                src={`${config.app.basePath}biasafe-logo.png`}
                alt="BIASafe Logo"
                className="h-[38px] w-auto"
              />
            </div>
          </div>

          {/* `flex-1` + `justify-center` is what vertically centres a short
              screen. The two later screens used `py-[56px]` on a plain column,
              which left them anchored to the top of a full-height panel while
              Login and SignUp sat in the middle — the most visible half of the
              inconsistency. */}
          <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-8 sm:py-10 md:py-12 lg:py-14 xl:py-16">
            <div className="w-full flex flex-col items-center">
              <div className="content-stretch flex flex-col gap-[18px] items-center relative shrink-0 w-full min-w-[144px] max-w-[256px] sm:max-w-[360px] md:max-w-[410px] lg:max-w-[460px] xl:max-w-[540px]">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface AuthHeadingProps {
  title: string;
  /** Sits under the title, centred. Omit for a title-only screen. */
  children?: ReactNode;
  /** Small uppercase line above the title, e.g. the two-factor "Required". */
  eyebrow?: ReactNode;
}

/**
 * Title and supporting copy, centred — the block Login and SignUp both open
 * with. 42px serif over 16px sans, `gap-[8px]`, `text-center`.
 */
export function AuthHeading({ title, children, eyebrow }: AuthHeadingProps) {
  return (
    <div
      className="content-stretch flex flex-col gap-[8px] items-center leading-[0] relative shrink-0 text-center w-full"
      data-name="header-text"
    >
      {eyebrow}
      <div
        className="flex flex-col font-medium justify-center relative shrink-0 text-[#121212] text-[48px] tracking-[-1.5px] w-full"
        style={{ fontFamily: FONT_SERIF }}
      >
        <p className="leading-[48px] text-[42px]">{title}</p>
      </div>
      {children && (
        <div
          className="flex flex-col justify-center not-italic relative shrink-0 text-[#3e3e3e] text-[16px] w-full"
          style={{ fontFamily: FONT_SANS }}
        >
          <p className="leading-[24px]">{children}</p>
        </div>
      )}
    </div>
  );
}

export interface AuthPrimaryButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}

/**
 * The primary action, black.
 *
 * Same markup as Login's "Log In" button, including the `#d1d5db` disabled
 * state — the later screens used the shadcn `<Button>`, which renders the
 * `#7F22FE` brand purple, so one flow showed the user two different primary
 * buttons.
 */
export function AuthPrimaryButton({
  children,
  onClick,
  type = 'button',
  disabled,
}: AuthPrimaryButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      // Same reason as OtpInput: MfaEnrollment renders this button on the
      // themed Settings page as well as on the pinned-light auth screens, and
      // a near-black full-width button on a dark card is an invisible primary
      // action. `--ink`, whose LIGHT values are the Figma hexes verbatim, so
      // these screens are unchanged and the button still inverts where it
      // has to.
      className={`relative w-full shrink-0 rounded-lg transition-colors ${
        disabled
          ? 'bg-ink-disabled cursor-not-allowed'
          : 'bg-ink hover:bg-ink/90 cursor-pointer'
      }`}
    >
      <div className="flex flex-row items-center justify-center size-full">
        <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[16px] py-[8px] relative w-full">
          <div
            className={`relative flex flex-col justify-center text-[16px] leading-[0] text-nowrap not-italic shrink-0 ${disabled ? 'text-muted-foreground' : 'text-ink-foreground'}`}
            style={{ fontFamily: FONT_SANS }}
          >
            <p className="leading-[24px] whitespace-pre">{children}</p>
          </div>
        </div>
      </div>
    </button>
  );
}
